/**
 * Разбор отсканированного кода для «Проверки заказов».
 *
 * Четыре варианта (см. этикетку короба):
 *  - `ean13`      — 13-значный ШК упаковки/единицы.
 *  - `datamatrix` — ЧЗ-DataMatrix единички (GS1 AI 01+21 serial +93 crypto).
 *  - `gs1`        — GS1-DataMatrix упаковки (AI 01 GTIN + 10 партия + 30|37 кол-во,
 *                   БЕЗ 21 — это НЕ честный знак).
 *  - `gtin`       — GS1 только с GTIN (01) без партии/serial → единичный GTIN.
 *
 * Различаем ЧЗ vs упаковочный GS1 по содержимому: AI 21 (serial) → честный знак;
 * AI 10 (партия) без 21 → упаковка.
 */
import { HRI_START, parseDataMatrix } from "./dm-parser";
import { isValidEan13 } from "./ean13";

const GS = "\x1d";

// AI с фиксированной длиной значения (нужны, чтобы парсить строку без GS).
const FIXED_AI: Record<string, number> = {
  "00": 18,
  "01": 14,
  "02": 14,
  "11": 6,
  "12": 6,
  "13": 6,
  "15": 6,
  "16": 6,
  "17": 6,
};

export type OrderScanKind =
  | "ean13"
  | "gtin"
  | "datamatrix"
  | "gs1"
  | "sscc"
  | "unknown";

export interface OrderScan {
  kind: OrderScanKind;
  raw: string;
  ean13?: string;
  gtin?: string;
  serial?: string;
  batch?: string;
  qty?: number;
  /** Номер коробки портала (AI 00), 18 цифр. */
  sscc?: string;
}

/**
 * Разбор GS1-element-string в карту `{AI: value}`. Понимает обе формы:
 * сырую (с разделителями GS `\x1d` для переменных полей) и human-readable
 * `(AI)value(AI)value`.
 */
export function parseGs1(raw: string): Record<string, string> {
  const pairs: Record<string, string> = {};
  // Скобочная форма — только с `(AI)` в начале: скобка внутри серийника ЧЗ
  // (`…215ph)(F…`) — это символ серийника, а не разметка (см. `dm-parser.ts`).
  if (HRI_START.test(raw)) {
    const re = /\((\d{2,4})\)?([^(]*)/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(raw)) !== null) {
      if (m[1]) pairs[m[1]] = (m[2] || "").trim();
    }
    return pairs;
  }
  let i = 0;
  while (i + 2 <= raw.length) {
    const ai = raw.slice(i, i + 2);
    if (!/^\d{2}$/.test(ai)) break;
    i += 2;
    const fixed = FIXED_AI[ai];
    if (fixed) {
      pairs[ai] = raw.slice(i, i + fixed);
      i += fixed;
    } else {
      const gsIdx = raw.indexOf(GS, i);
      const end = gsIdx === -1 ? raw.length : gsIdx;
      pairs[ai] = raw.slice(i, end);
      i = gsIdx === -1 ? raw.length : gsIdx + 1;
    }
  }
  return pairs;
}

/** Номер коробки: 18 голых цифр или AI 00 в element string. */
function detectSscc(raw: string): string | undefined {
  if (/^\d{18}$/.test(raw)) return raw;
  const pairs = parseGs1(raw);
  const v = pairs["00"];
  return v && /^\d{18}$/.test(v) ? v : undefined;
}

/** Главный разбор отсканированного кода. */
export function parseScan(raw0: string): OrderScan {
  const raw = (raw0 || "").trim();
  if (!raw) return { kind: "unknown", raw };

  // ⚠️ Коробка портала — ПЕРВОЙ. Её номер это 18 цифр, а наклейка приезжает
  // element string'ом `00<SSCC>…`; обе формы более общий разбор не узнаёт
  // вовсе (не 13 цифр, нет AI 01), и скан молча становился `unknown` — то
  // есть до бэкенда не доходил, и коробку не опознавал ни один экран.
  //
  // Контрольную цифру здесь НЕ проверяем намеренно: правило mod-10 живёт на
  // бэке в одном месте (`boxes/sscc.py`), и дублировать его во второй язык
  // значит завести две реализации, которые однажды разойдутся. Чужой
  // 18-значный код бэк отобьёт понятным текстом.
  const sscc = detectSscc(raw);
  if (sscc) return { kind: "sscc", raw, sscc };

  // Чистый EAN13.
  if (/^\d{13}$/.test(raw) && isValidEan13(raw)) {
    return { kind: "ean13", raw, ean13: raw };
  }

  // Честный знак (01/21/93) — проверенный парсер (поддерживает GS и human-readable).
  const dm = parseDataMatrix(raw);
  if (dm) {
    return { kind: "datamatrix", raw, gtin: dm.gtin, serial: dm.serial };
  }

  // Общий GS1: упаковка (01 + 10 [+30/37]) или одиночный GTIN (только 01).
  const pairs = parseGs1(raw);
  const gtin = pairs["01"];
  if (gtin && /^\d{14}$/.test(gtin)) {
    const batch = pairs["10"];
    if (batch) {
      const qtyRaw = pairs["30"] ?? pairs["37"];
      const qty = qtyRaw && /^\d+$/.test(qtyRaw) ? Number(qtyRaw) : undefined;
      return { kind: "gs1", raw, gtin, batch, qty };
    }
    // только GTIN → единичный товар
    if (!pairs["21"]) {
      return { kind: "gtin", raw, gtin };
    }
  }

  // Прочие цифровые коды (8..14) трактуем как GTIN/EAN.
  if (/^\d{8,14}$/.test(raw)) {
    return raw.length === 13 ? { kind: "ean13", raw, ean13: raw } : { kind: "gtin", raw, gtin: raw };
  }

  return { kind: "unknown", raw };
}
